import React from 'react';
import {
    BadgeProps,
    useBreakpoint,
    Group,
    Badge,
    Tooltip,
    ConditionalWrapper,
    Button,
    FiltersOutlined,
} from '@landr/maestro';
import { css } from '@landr/styled-system/css';
import { t } from '@lingui/macro';
import { useCurrentSearchState } from 'controllers/SearchController/hooks/useCurrentSearchState';
import { useSearchController } from 'controllers/SearchController/SearchContext';
import { useSearchBarContext } from 'components/Filters/context/SearchBarContext/SearchBarContext';
import { useModalContext } from 'contexts/ModalContext/ModalContext';
import { OpenModal } from 'contexts/ModalContext/ModalContext.type';
import useAdvancedFilter from '../SearchTools/hooks/useAdvancedFilter';
import useAdvancedFiltersToggle from './useAdvancedFiltersToggle';

const _AdvancedFiltersToggle = ({
    isDisabled,
    size,
    total,
    isLoading,
    openModal,
    noAdvancedFiltersAvailable,
}: {
    isDisabled: boolean;
    size: BadgeProps['size'];
    total: number;
    isLoading: boolean;
    openModal: OpenModal;
    noAdvancedFiltersAvailable: boolean;
}) => (
    <ConditionalWrapper
        condition={isDisabled}
        wrapper={(children: React.ReactNode): React.ReactNode => (
            <Tooltip label={t`Filters unavailable`} hasArrow position="top">
                {/**
                 * Needs to wrap in a hidden badge for appropriate size.
                 * Button doesn't trigger tooltip when disabled
                 * https://github.com/LandrAudio/LANDR.Maestro/issues/1460
                 **/}
                <Badge size={size} visibility="hidden">
                    {children}
                </Badge>
            </Tooltip>
        )}
    >
        <Tooltip label={t`Other filters`} position="top">
            <Button
                onClick={() => openModal.advancedFiltersModal()}
                variant="ghost"
                isDisabled={
                    isDisabled || isLoading || noAdvancedFiltersAvailable
                }
                isRounded
                size={size}
                className={css({
                    _pressed: {
                        backgroundColor: 'surface.high',
                    },
                    minWidth: 'auto',
                })}
            >
                <Group gap="xs">
                    <Group.Item>
                        <FiltersOutlined />
                    </Group.Item>

                    {!!total && !isDisabled && (
                        <Group.Item display="flex" alignSelf="center">
                            <Badge
                                label={total.toString()}
                                variant="inverted"
                                isRounded
                                size="sm"
                            />
                        </Group.Item>
                    )}
                </Group>
            </Button>
        </Tooltip>
    </ConditionalWrapper>
);

const AdvancedFiltersToggle: React.FC = () => {
    const isMobile = useBreakpoint('md');
    const { openModal } = useModalContext();

    const { isSearchBarDisabled } = useSearchBarContext();

    const {
        bpmlowerbound,
        bpmupperbound,
        genres,
        instruments,
        keyquality,
        keyrootnote,
        sfxs,
    } = useCurrentSearchState();

    const { filtersLoading, samplesLoading, packsLoading } =
        useSearchController();

    const isLoading = filtersLoading || samplesLoading || packsLoading;

    const { total, size } = useAdvancedFiltersToggle({
        bpmlowerbound,
        bpmupperbound,
        genres,
        instruments,
        keyquality,
        keyrootnote,
        sfxs,
        isMobile,
        isLoading,
    });

    const { noAdvancedFiltersAvailable } = useAdvancedFilter();

    return (
        <_AdvancedFiltersToggle
            size={size}
            total={total}
            isDisabled={isSearchBarDisabled}
            isLoading={isLoading}
            openModal={openModal}
            noAdvancedFiltersAvailable={noAdvancedFiltersAvailable}
        />
    );
};

export default AdvancedFiltersToggle;
